import React from 'react';
import { useNavigate } from 'react-router-dom';

const WelcomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-jet-gray-50 pt-[70px]">
      <main className="py-16">
        <div className="w-[86%] mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-jet-yellow rounded-full mb-6">
              <svg className="w-12 h-12 text-jet-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
              </svg>
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-bold text-jet-gray-900 mb-6">
              Welcome to <span className="text-jet-yellow">JetScanner</span>
            </h1>
            
            <p className="text-xl text-jet-gray-600 mb-8 max-w-3xl mx-auto">
              Your gateway to seamless private aviation. Discover luxury flights, 
              book with confidence, and experience the future of travel.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/search-flight')}
                className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-semibold py-4 px-8 rounded-lg transition-colors text-lg"
              >
                Start Your Journey
              </button>
              <button
                onClick={() => navigate('/how-it-works')}
                className="border-2 border-jet-gray-300 hover:border-jet-gray-400 text-jet-gray-700 font-semibold py-4 px-8 rounded-lg transition-colors text-lg"
              >
                Learn More
              </button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-jet-blue-100 rounded-full mb-6">
                <svg className="w-8 h-8 text-jet-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-jet-gray-900 mb-4">Smart Search</h3>
              <p className="text-jet-gray-600">
                Find the perfect flight with our intelligent search engine. 
                Compare prices, routes, and amenities in real-time.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-jet-green-100 rounded-full mb-6">
                <svg className="w-8 h-8 text-jet-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-jet-gray-900 mb-4">Secure Booking</h3>
              <p className="text-jet-gray-600">
                Book with confidence using our secure payment system. 
                Your data is protected with industry-leading encryption.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-jet-purple-100 rounded-full mb-6">
                <svg className="w-8 h-8 text-jet-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-jet-gray-900 mb-4">Instant Confirmation</h3>
              <p className="text-jet-gray-600">
                Get immediate booking confirmation and digital tickets. 
                Manage your flights from anywhere, anytime.
              </p>
            </div>
          </div>

          {/* Statistics */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-jet-yellow mb-2">10K+</div>
                <div className="text-jet-gray-600">Happy Customers</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-jet-yellow mb-2">500+</div>
                <div className="text-jet-gray-600">Aircraft Partners</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-jet-yellow mb-2">1000+</div>
                <div className="text-jet-gray-600">Destinations</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-jet-yellow mb-2">24/7</div>
                <div className="text-jet-gray-600">Support</div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-jet-gray-900 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Ready to Take Off?</h2>
            <p className="text-jet-gray-300 mb-6">
              Join thousands of travelers who trust JetScanner for their private aviation needs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/search-flight')}
                className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Search Flights
              </button>
              <button
                onClick={() => navigate('/about-us')}
                className="border border-jet-gray-600 hover:border-jet-gray-500 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                About Us
              </button>
              <button
                onClick={() => navigate('/contact-us')}
                className="border border-jet-gray-600 hover:border-jet-gray-500 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WelcomePage;